// src/modules/products/components/ProductDetailPage.tsx
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { message } from "antd";
import { useProducts } from "../hooks/useProducts";
import { useProductForm } from "../hooks/useProductForm";
import {
  handleDateTime,
  formatApiDateTime,
} from "@modules/shared/utils/handle-date.utils";
import {
  applyDiscount,
  formatEnumLabel,
  formatMoney,
  getPrimaryImage,
  getStatusBadgeClass,
  getPriceInputProps,
  parseOptionText,
} from "@modules/shared/utils/formatters";
import { PaymentMethod, ProductStatus } from "../models/product.model";
import { DEFAULT_CATEGORIES } from "../constants/product.constants";
import { ProductDetailSkeleton } from "./skeleton/ProductDetailSkeleton";

const productStatuses: ProductStatus[] = [
  "TRACKER",
  "UNLIMITED",
  "PRE_ORDER",
  "OUT_OF_STOCK",
];
const paymentMethods: PaymentMethod[] = ["CONTACT", "PAYOS"];

const parseNumberValue = (value: string) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

export default function ProductDetailPage() {
  const { storeId, productId } = useParams<{
    storeId: string;
    productId: string;
  }>();
  const navigate = useNavigate();
  const {
    products,
    shops,
    getProductById,
    getShopById,
    getBrandById,
    updateProduct,
    deleteProduct,
    loading,
    loaded,
    error,
  } = useProducts();

  const product = getProductById(productId || "");

  const {
    formState,
    existingImages,
    newImageFiles,
    handleFieldChange,
    handleImageFilesAdd,
    handleRemoveImage,
    handleMakeCover,
    handlePriceUnitChange,
    setWarranty,
  } = useProductForm(product);

  const [submitting, setSubmitting] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  const [warrantyEnabled, setWarrantyEnabled] = useState<boolean>(
    () => !!formState.warranty_unit,
  );

  const shop = formState.shop_id ? getShopById(formState.shop_id) : undefined;
  const brand = shop ? getBrandById(shop.b2b_id) : undefined;

  const coverImage = getPrimaryImage(formState.images.map((img) => img.url));
  const priceProps = getPriceInputProps(formState.price_unit);
  const retailSalePrice = applyDiscount(
    formState.price,
    parseNumberValue(formState.discount_percentage),
  );

  useEffect(() => {
    setWarrantyEnabled(!!formState.warranty_unit);
  }, [formState.warranty_unit]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    const sameShop = products.filter(
      (p) => p.id !== product.id && p.shop_id === product.shop_id,
    );
    if (sameShop.length) return sameShop.slice(0, 4);
    if (brand) {
      const relatedShopIds = shops
        .filter((s) => s.b2b_id === brand.id)
        .map((s) => s.id);
      return products
        .filter((p) => p.id !== product.id && relatedShopIds.indexOf(p.shop_id))
        .slice(0, 4);
    }
    return products.filter((p) => p.id !== product.id).slice(0, 4);
  }, [product, products, brand, shops]);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) handleImageFilesAdd(Array.from(files));
    e.target.value = "";
  };

  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!product) return;
    const stockValue =
      formState.stock.trim() === ""
        ? product.stock
        : parseNumberValue(formState.stock);
    if (
      warrantyEnabled &&
      formState.warranty !== null &&
      (formState.warranty < 1 || !Number.isInteger(formState.warranty))
    ) {
      message.error("Bảo hành phải là số nguyên dương (>= 1)");
      return;
    }
    setSubmitting(true);
    try {
      await updateProduct(product.id, {
        name: formState.name.trim() || product.name,
        shop_id: formState.shop_id || product.shop_id,
        category: formState.category.trim() || product.category,
        price_unit: formState.price_unit.trim() || product.price_unit,
        price: formState.price.trim() || product.price,
        existing_images: existingImages,
        new_image_files: newImageFiles,
        options: parseOptionText(formState.optionsText),
        description: {
          en: formState.description_en.trim(),
          vi: formState.description_vi.trim(),
        },
        stock: stockValue,
        status: formState.status,
        detail_information: {
          warranty: warrantyEnabled ? formState.warranty : null,
          warranty_unit: warrantyEnabled
            ? formState.warranty_unit.trim() || null
            : null,
          origin: formState.origin.trim() || product.detail_information.origin,
          send_from:
            formState.send_from.trim() || product.detail_information.send_from,
        },
        discount_percentage: parseNumberValue(formState.discount_percentage),
        payment_method: formState.payment_method,
        trade_discount_percentage: 0,
      });
      setLastSavedAt(handleDateTime(new Date()));
      message.success("Cập nhật sản phẩm thành công");
    } catch (err: any) {
      message.error(err.message || "Cập nhật thất bại");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!product) return;
    if (
      window.confirm(
        `Xóa sản phẩm "${product.name}"? Hành động này không thể hoàn tác.`,
      )
    ) {
      await deleteProduct(product.id);
      message.success("Đã xóa sản phẩm");
      const shopId = product.shop_id;
      if (shopId) {
        navigate(`/b2b/stores/${shopId}/products`);
      } else {
        navigate("/b2b/stores");
      }
    }
  };

  if (loading && !loaded) {
    return <ProductDetailSkeleton />;
  }

  if (!product) {
    return (
      <div className="card border-0 shadow-sm mt-4">
        <div className="card-body text-center text-muted">
          Không tìm thấy sản phẩm.
        </div>
      </div>
    );
  }

  return (
    <>
      {error && <div className="alert alert-danger mt-4 mb-0">{error}</div>}

      {/* Header thông tin sản phẩm */}
      <div className="card border-0 shadow-sm mt-4 overflow-hidden">
        <div className="row g-0">
          <div className="col-lg-5">
            <div className="h-100 bg-light p-4">
              <img
                src={coverImage}
                alt={formState.name || product.name}
                className="img-fluid rounded-3 w-100"
                style={{ maxHeight: 420, objectFit: "cover" }}
              />
              <div className="row g-2 mt-2">
                {formState.images.slice(1, 5).map((img, idx) => (
                  <div className="col-3" key={img.id}>
                    <div className="border rounded-3 overflow-hidden bg-white">
                      <img
                        src={img.url}
                        alt={`Thư viện ${idx + 1}`}
                        className="img-fluid w-100"
                        style={{ height: 72, objectFit: "cover" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="col-lg-7">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                <div>
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    <span
                      className={`badge ${getStatusBadgeClass(product.status)}`}
                    >
                      {formatEnumLabel(product.status)}
                    </span>
                    <span className="badge bg-soft-primary text-primary">
                      {formatEnumLabel(product.payment_method)}
                    </span>
                    <span className="badge bg-soft-secondary text-secondary">
                      {product.category}
                    </span>
                    {shop && (
                      <span className="badge bg-soft-info text-info">
                        {shop.name}
                      </span>
                    )}
                  </div>
                  <h4 className="mb-1">{product.name}</h4>
                  <p className="text-muted mb-0">
                    {shop?.name ?? "Cửa hàng không xác định"}
                  </p>
                  {brand && (
                    <Link
                      to={`/b2b/brands/${brand.id}`}
                      state={{ breadcrumbLabel: brand.name }}
                      className="small"
                    >
                      Mở thương hiệu: {brand.name}
                    </Link>
                  )}
                </div>
                <div className="text-lg-end">
                  <div className="text-muted small">Cập nhật</div>
                  <div className="fw-semibold">
                    {formatApiDateTime(product.updated_at)}
                  </div>
                </div>
              </div>

              <div className="row g-3 mt-1">
                <div className="col-md-4">
                  <div className="border rounded-3 p-3">
                    <div className="text-muted small">Giá gốc</div>
                    <h5 className="mb-0 mt-1">
                      {formatMoney(formState.price, formState.price_unit)}
                    </h5>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="border rounded-3 p-3">
                    <div className="text-muted small">
                      Giá bán áp đụng giảm giá
                    </div>
                    <h5 className="mb-0 mt-1">
                      {retailSalePrice !== null
                        ? formatMoney(retailSalePrice, formState.price_unit)
                        : "-"}
                    </h5>
                  </div>
                </div>
              </div>

              <div className="row g-3 mt-1">
                <div className="col-md-3">
                  <div className="border rounded-3 p-3">
                    <div className="text-muted small">Tồn kho</div>
                    <div className="fw-semibold">{product.stock}</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="border rounded-3 p-3">
                    <div className="text-muted small">Đã bán</div>
                    <div className="fw-semibold">{product.bought}</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="border rounded-3 p-3">
                    <div className="text-muted small">Ảnh</div>
                    <div className="fw-semibold">{formState.images.length}</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="border rounded-3 p-3">
                    <div className="text-muted small">Tùy chọn</div>
                    <div className="fw-semibold">{product.options.length}</div>
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center gap-3 mt-4">
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={handleDelete}
                >
                  Xóa sản phẩm
                </button>
                {lastSavedAt && (
                  <span className="text-muted small">
                    Đã lưu lúc {lastSavedAt}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form cấu hình sản phẩm */}
      <div className="row mt-4">
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="mb-4">Cấu hình sản phẩm</h5>
              <form onSubmit={handleSave}>
                {/* Nhóm 1: Thông tin cơ bản */}
                <div className="card border-0 shadow-none bg-light p-3 mb-3">
                  <h6 className="mb-3">Thông tin cơ bản</h6>
                  <div className="row g-3">
                    <div className="col-md-8">
                      <label className="form-label">Tên sản phẩm *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formState.name}
                        onChange={(e) =>
                          handleFieldChange("name", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Danh mục *</label>
                      <select
                        className="form-select"
                        value={formState.category}
                        onChange={(e) =>
                          handleFieldChange("category", e.target.value)
                        }
                        required
                      >
                        <option value="">Chọn danh mục</option>
                        {DEFAULT_CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Cửa hàng *</label>
                      <select
                        className="form-select"
                        value={formState.shop_id}
                        onChange={(e) =>
                          handleFieldChange("shop_id", e.target.value)
                        }
                        required
                      >
                        {shops.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Nhóm 2: Giá cả & tồn kho */}
                <div className="card border-0 shadow-none bg-light p-3 mb-3">
                  <h6 className="mb-3">Giá cả & tồn kho</h6>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label">Đơn vị tiền tệ</label>
                      <select
                        className="form-select"
                        value={formState.price_unit}
                        onChange={(e) => handlePriceUnitChange(e.target.value)}
                      >
                        <option value="VND">VND</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Giá *</label>
                      <input
                        type="number"
                        min={priceProps.min}
                        step={priceProps.step}
                        className="form-control"
                        value={formState.price}
                        onChange={(e) =>
                          handleFieldChange("price", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Tồn kho</label>
                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        value={formState.stock}
                        onChange={(e) =>
                          handleFieldChange("stock", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Đã bán</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formState.bought}
                        disabled
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Giảm giá %</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        className="form-control"
                        value={formState.discount_percentage}
                        onChange={(e) =>
                          handleFieldChange(
                            "discount_percentage",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Nhóm 3: Trạng thái & thanh toán */}
                <div className="card border-0 shadow-none bg-light p-3 mb-3">
                  <h6 className="mb-3">Trạng thái & thanh toán</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Trạng thái</label>
                      <select
                        className="form-select"
                        value={formState.status}
                        onChange={(e) =>
                          handleFieldChange(
                            "status",
                            e.target.value as ProductStatus,
                          )
                        }
                      >
                        {productStatuses.map((s) => (
                          <option key={s} value={s}>
                            {formatEnumLabel(s)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        Phương thức thanh toán
                      </label>
                      <select
                        className="form-select"
                        value={formState.payment_method}
                        onChange={(e) =>
                          handleFieldChange(
                            "payment_method",
                            e.target.value as PaymentMethod,
                          )
                        }
                      >
                        {paymentMethods.map((m) => (
                          <option key={m} value={m}>
                            {formatEnumLabel(m)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Nhóm 4: Mô tả */}
                <div className="card border-0 shadow-none bg-light p-3 mb-3">
                  <h6 className="mb-3">Mô tả</h6>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Mô tả (EN)</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={formState.description_en}
                        onChange={(e) =>
                          handleFieldChange("description_en", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Mô tả (VI)</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={formState.description_vi}
                        onChange={(e) =>
                          handleFieldChange("description_vi", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Nhóm 5: Thông tin chi tiết */}
                <div className="card border-0 shadow-none bg-light p-3 mb-3">
                  <h6 className="mb-3">Thông tin chi tiết</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Xuất xứ *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formState.origin}
                        onChange={(e) =>
                          handleFieldChange("origin", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Nơi gửi từ *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formState.send_from}
                        onChange={(e) =>
                          handleFieldChange("send_from", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="col-12">
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-1">
                        <label className="form-label fw-semibold mb-0">
                          Bảo hành
                        </label>
                        <div className="form-check form-switch mb-0 d-flex align-items-center">
                          <input
                            type="checkbox"
                            className="form-check-input flex-shrink-0"
                            id="edit-product-warranty-enabled"
                            checked={warrantyEnabled}
                            onChange={(e) => {
                              const enabled = e.target.checked;
                              setWarrantyEnabled(enabled);
                              if (!enabled) {
                                setWarranty(null);
                                handleFieldChange("warranty_unit", "");
                              } else if (!formState.warranty_unit) {
                                handleFieldChange("warranty_unit", "MONTH");
                              }
                            }}
                            style={{
                              width: 48,
                              height: 24,
                              cursor: "pointer",
                              marginTop: 0,
                              marginBottom: 0,
                              flexShrink: 0,
                            }}
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="edit-product-warranty-enabled"
                            style={{
                              marginBottom: 0,
                              lineHeight: "24px",
                              height: 24,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {warrantyEnabled ? "Đã bật" : "Tắt"}
                          </label>
                        </div>
                      </div>
                      <div className="form-text mb-2">
                        {warrantyEnabled
                          ? "Thiết lập thời gian bảo hành cho sản phẩm"
                          : "Sản phẩm không được bảo hành"}
                      </div>
                    </div>
                    {warrantyEnabled && (
                      <>
                        <div className="col-md-6">
                          <label className="form-label">
                            Thời gian bảo hành
                          </label>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            className="form-control"
                            value={formState.warranty ?? ""}
                            onChange={(e) =>
                              setWarranty(
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value),
                              )
                            }
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Đơn vị bảo hành</label>
                          <select
                            className="form-select"
                            value={formState.warranty_unit}
                            onChange={(e) =>
                              handleFieldChange("warranty_unit", e.target.value)
                            }
                          >
                            <option value="DAY">Ngày</option>
                            <option value="MONTH" selected>
                              Tháng
                            </option>
                            <option value="YEAR">Năm</option>
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Nhóm 6: Tùy chọn (ẩn đi) */}
                <div
                  className="card border-0 shadow-none bg-light p-3"
                  style={{ display: "none" }}
                >
                  <h6 className="mb-3">Tùy chọn (key: value1, value2)</h6>
                  <div className="row g-3">
                    <div className="col-12">
                      <textarea
                        className="form-control"
                        rows={4}
                        value={formState.optionsText}
                        onChange={(e) =>
                          handleFieldChange("optionsText", e.target.value)
                        }
                        placeholder="Ví dụ: Màu sắc: Đỏ, Xanh, Vàng"
                      />
                    </div>
                  </div>
                </div>

                {/* Nút hành động */}
                <div className="d-flex align-items-center gap-3 mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? "Đang lưu..." : "Lưu thay đổi"}
                  </button>
                  <Link
                    to={`/b2b/stores/${storeId || product.shop_id}/products`}
                    className="btn btn-light"
                  >
                    Quay lại danh sách sản phẩm
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Cột phải: Thông tin sản phẩm & Thư viện ảnh */}
        <div className="col-lg-4 mt-4 mt-lg-0">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="mb-3">Thông tin sản phẩm</h6>
              <div className="d-flex flex-column gap-2">
                <div className="d-flex justify-content-between border rounded-3 p-3">
                  <span className="text-muted">Ngày tạo</span>
                  <span className="fw-semibold">
                    {formatApiDateTime(product.created_at)}
                  </span>
                </div>
                <div className="d-flex justify-content-between border rounded-3 p-3">
                  <span className="text-muted">Cập nhật</span>
                  <span className="fw-semibold">
                    {formatApiDateTime(product.updated_at)}
                  </span>
                </div>
                <div className="d-flex justify-content-between border rounded-3 p-3">
                  <span className="text-muted">Cửa hàng</span>
                  <span className="fw-semibold">{shop?.name ?? "-"}</span>
                </div>
                <div className="d-flex justify-content-between border rounded-3 p-3">
                  <span className="text-muted">Thương hiệu</span>
                  <span className="fw-semibold">{brand?.name ?? "-"}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mt-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Thư viện ảnh</h6>
                <span className="text-muted small">
                  {formState.images.length} ảnh
                </span>
              </div>
              <input
                type="file"
                className="form-control"
                accept="image/*"
                multiple
                onChange={handleImageChange}
              />
              <p className="text-muted small mt-2 mb-0">
                Ảnh đầu tiên là ảnh bìa.
              </p>
              {formState.images.length === 0 ? (
                <p className="text-muted small mt-3">Chưa tải ảnh nào.</p>
              ) : (
                <div className="d-flex flex-column gap-2 mt-3">
                  {formState.images.map((img, idx) => (
                    <div
                      className="d-flex align-items-center gap-3 border rounded-3 p-2"
                      key={img.id}
                    >
                      <img
                        src={img.url}
                        alt="thư viện"
                        className="rounded"
                        style={{ width: 56, height: 56, objectFit: "cover" }}
                      />
                      <div className="flex-grow-1">
                        <div className="fw-semibold small">
                          {idx === 0 ? "Ảnh bìa" : `Thư viện ${idx}`}
                        </div>
                      </div>
                      {idx > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleMakeCover(img.id)}
                        >
                          Đặt làm bìa
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleRemoveImage(img.id)}
                      >
                        Xóa
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sản phẩm liên quan */}
      {relatedProducts.length > 0 && (
        <div className="card border-0 shadow-sm mt-4">
          <div className="card-body">
            <h5 className="mb-3">Sản phẩm liên quan</h5>
            <div className="row">
              {relatedProducts.map((item) => (
                <div className="col-md-6 col-xl-3 mt-3" key={item.id}>
                  <div className="border rounded-3 h-100 overflow-hidden">
                    <img
                      src={getPrimaryImage(item.images)}
                      alt={item.name}
                      className="img-fluid w-100"
                      style={{ height: 160, objectFit: "cover" }}
                    />
                    <div className="p-3">
                      <div className="d-flex justify-content-between gap-2">
                        <h6 className="mb-1">{item.name}</h6>
                        <span
                          className={`badge ${getStatusBadgeClass(item.status)}`}
                        >
                          {formatEnumLabel(item.status)}
                        </span>
                      </div>
                      <p className="text-muted small mb-2">{item.category}</p>
                      <div className="fw-semibold mb-3">
                        {formatMoney(item.price, item.price_unit)}
                      </div>
                      <Link
                        to={`/b2b/stores/${item.shop_id}/products/${item.id}`}
                        state={{ breadcrumbLabel: item.name }}
                        className="btn btn-sm btn-outline-primary"
                      >
                        Xem chi tiết
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
